/**
 * JobMockAI — MilestoneTimeline (Phase 4 Step 8)
 *
 * Vertical timeline of earned milestones with dates, ordered chronologically.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Typography,
} from '@mui/material';
import { CheckCircle, EmojiEvents, Timeline } from '@mui/icons-material';

const MilestoneTimeline = ({ milestones = [], loading }) => {
  if (loading) {
    return (
      <Card variant="outlined" data-testid="milestone-timeline-loading">
        <CardContent>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  const earned = milestones
    .filter((m) => m.completed_at || m.completedAt)
    .sort((a, b) => new Date(b.completed_at || b.completedAt) - new Date(a.completed_at || a.completedAt));

  if (!earned.length) {
    return (
      <Card variant="outlined" data-testid="milestone-timeline-empty">
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Timeline sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No milestones earned yet. Keep practicing!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" data-testid="milestone-timeline">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Timeline color="primary" />
          <Typography variant="h6" fontWeight={700}>Achievement Timeline</Typography>
        </Box>

        {earned.map((m, idx) => {
          const date = new Date(m.completed_at || m.completedAt).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
          });
          return (
            <Box
              key={m.id || m.key || idx}
              sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'flex-start' }}
              data-testid={`timeline-item-${idx}`}
            >
              {/* Timeline dot + line */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24 }}>
                <CheckCircle color="success" sx={{ fontSize: 20 }} />
                {idx < earned.length - 1 && (
                  <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0.5, minHeight: 20 }} />
                )}
              </Box>
              {/* Content */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {m.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {date}
                </Typography>
                {m.xp_reward > 0 && (
                  <Chip label={`+${m.xp_reward} XP`} size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                )}
              </Box>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default MilestoneTimeline;
